import React from 'react';
import { Link } from 'react-router-dom';

const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const themeHoverClasses = {
    orange: 'hover:border-orange-300',
    blue: 'hover:border-blue-300',
    purple: 'hover:border-purple-300',
};

const themeBadgeClasses = {
    orange: 'bg-orange-100 text-orange-900 ring-orange-200/80',
    blue: 'bg-blue-100 text-blue-900 ring-blue-200/80',
    purple: 'bg-purple-100 text-purple-900 ring-purple-200/80',
};

const themeFeaturedCardClasses = {
    orange: 'border-2 border-orange-200 shadow-md ring-1 ring-orange-100/60',
    blue: 'border-2 border-blue-200 shadow-md ring-1 ring-blue-100/60',
    purple: 'border-2 border-purple-200 shadow-md ring-1 ring-purple-100/60',
};

const FacultyAdvisorCard = ({
    name,
    affiliation,
    photoUrl,
    link,
    theme = 'blue',
    roleLabel,
    featured = false,
    /** Subtle outline only — same card size as peers (e.g. keynote in faculty grid). */
    rimHighlight = false,
    /** Short label at bottom of card (e.g. keynote reminder); pairs with rimHighlight. */
    footerBadge,
}) => {
    const hoverClass = themeHoverClasses[theme] ?? themeHoverClasses.blue;
    const badgeClass = themeBadgeClasses[theme] ?? themeBadgeClasses.blue;
    const avatarSize = featured ? 'w-44 h-44' : 'w-32 h-32';
    const initialsSize = featured ? 'text-3xl' : 'text-2xl';
    const titleClass = featured ? 'text-xl md:text-2xl' : 'text-lg';
    const padding = featured ? 'p-8 md:p-10' : 'p-6';
    const cardBorder = featured
        ? (themeFeaturedCardClasses[theme] ?? themeFeaturedCardClasses.blue)
        : 'border border-gray-200 shadow-sm';
    const rimClass =
        rimHighlight && !featured
            ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white'
            : '';

    const cardContent = (
        <div className="flex flex-col items-center text-center">
            {roleLabel && (
                <span
                    className={`inline-flex items-center mb-4 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ring-1 ${badgeClass}`}
                >
                    {roleLabel}
                </span>
            )}
            {photoUrl ? (
                <div
                    className={`${avatarSize} shrink-0 mx-auto rounded-full overflow-hidden border border-gray-200 shadow-md mb-4 relative`}
                >
                    <img
                        src={photoUrl}
                        alt={name}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        style={{
                            objectFit: 'cover',
                            objectPosition: 'center',
                            transform: 'scale(1.15)',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </div>
            ) : (
                <div
                    className={`${avatarSize} rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 border border-gray-200 flex items-center justify-center shadow-md mb-4`}
                >
                    <span className={`${initialsSize} font-semibold text-gray-700`}>{getInitials(name)}</span>
                </div>
            )}
            <h3 className={`${titleClass} font-semibold text-gray-900 mb-2 break-words`}>{name}</h3>
            <p className="text-sm text-gray-600 break-words leading-relaxed">{affiliation}</p>
            {footerBadge && (
                <span
                    className={`inline-flex items-center mt-4 px-3 py-1.5 rounded-full text-xs font-semibold text-center leading-snug ring-1 ${badgeClass}`}
                >
                    {footerBadge}
                </span>
            )}
        </div>
    );

    if (link) {
        const outerClass = `block bg-white rounded-xl ${cardBorder} hover:shadow-lg transition-all ${padding} ${hoverClass} ${rimClass}`;
        if (link.startsWith('/')) {
            return (
                <Link to={link} className={outerClass}>
                    {cardContent}
                </Link>
            );
        }
        return (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className={outerClass}
            >
                {cardContent}
            </a>
        );
    }

    return (
        <div
            className={`bg-white rounded-xl ${cardBorder} hover:shadow-md transition-all ${padding} ${hoverClass} ${rimClass}`}
        >
            {cardContent}
        </div>
    );
};

export default FacultyAdvisorCard;
